import type { FC } from 'react';
import { Edit2, Global, Location, Trash } from 'iconsax-react';
import {
  EditableInputCell,
  EditableInputCellEditMode,
  EditableInputCellViewMode,
} from '@src/shared/components/editable-input-cell';
import { SingleDeleteAction } from '@src/design-system/molecules/ticlick-table/actions';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@src/design-system/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';
import type { TPlacementHostCompany } from '@shared/types/placement-host-company';
import { hostCompaniesApi } from '@features/placements/services/host-companies-api';
import { CreateHostModal } from '../create-host-modal';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@src/design-system/tooltip';

const ViewHostHeader: FC<{ host: TPlacementHostCompany }> = ({ host }) => {
  const { mutateAsync: deleteHost, isPending } = hostCompaniesApi.useDelete();
  const { mutate: updateHost } = hostCompaniesApi.useUpdate();

  const addressLine = host?.address_line_1 ?? host?.address_line_2 ?? '';
  const addressTooltip = [host?.address_line_1, host?.address_line_2]
    .filter((line) => line?.trim())
    .join(', ');

  return (
    <div className='p-4 rounded-t-xl space-y-10'>
      <div className='flex items-center justify-between'>
        <span className='text-gray-900 font-medium text-lg'>Host Company</span>
        <div className='flex items-center gap-2'>
          <SingleDeleteAction
            id={host.id}
            label='host company'
            isLoading={isPending}
            onConfirm={(closeModal) =>
              deleteHost({ id: host.id }, { onSuccess: closeModal })
            }
            triggerComponent={
              <div className='group flex items-center cursor-pointer border p-2 border-primary-200 rounded-md'>
                <span className='text-primary-1000 group-hover:mr-1 font-medium text-[11px] opacity-0 -translate-x-1 max-w-0 overflow-hidden transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:max-w-[60px]'>
                  Delete
                </span>
                <Trash
                  className='text-primary-1000'
                  size={16}
                />
              </div>
            }
          />
          <Dialog>
            <DialogTrigger className='cursor-pointer'>
              <div className='group flex items-center cursor-pointer border p-2 border-primary-200 rounded-md'>
                <span className='text-primary-1000 group-hover:mr-1 font-medium text-[11px] opacity-0 -translate-x-1 max-w-0 overflow-hidden transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:max-w-[60px]'>
                  Edit
                </span>
                <Edit2
                  className='text-primary-1000'
                  size={16}
                />
              </div>
            </DialogTrigger>
            <DialogContent className='max-w-[600px]'>
              <CreateHostModal
                host={host}
                isTableView
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <Avatar className='h-20! w-20! rounded-lg border border-primary-200'>
            <AvatarImage
              src={host?.logo ?? undefined}
              alt={host?.name}
            />
            <AvatarFallback
              colorId={host?.id ?? 0}
              className='rounded-lg'
            >
              <span className='text-white text-2xl font-medium'>
                {host?.name.slice(0, 2).toUpperCase()}
              </span>
            </AvatarFallback>
          </Avatar>
          <div className='flex flex-col gap-1'>
            <span className='text-gray-900 font-medium text-lg'>
              {host?.name}
            </span>
            <div className='flex items-center gap-6 flex-wrap'>
              {(host?.address_line_1 || host?.address_line_2) && (
                <div className='flex items-center gap-0.5 min-w-0 max-w-[200px]'>
                  <Location
                    className='text-secondary-1000 shrink-0'
                    size={16}
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className='text-gray-800 font-normal text-sm truncate min-w-0 cursor-default'>
                        {addressLine}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      side='top'
                      className='max-w-xs break-words whitespace-normal'
                    >
                      {addressTooltip || addressLine}
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
              <div className='flex items-center min-w-0'>
                <Global
                  className='text-secondary-1000 shrink-0'
                  size={16}
                />
                <div className='relative flex-1 min-w-[200px] min-h-[30px]'>
                  <EditableInputCell className='min-w-0'>
                    <EditableInputCellEditMode
                      value={host?.website ?? ''}
                      onBlur={(e) => {
                        const newValue = e.target.value.trim();
                        if (newValue === (host?.website ?? '').trim()) return;
                        updateHost({
                          id: host.id,
                          website: newValue || undefined,
                        });
                      }}
                    />
                    <EditableInputCellViewMode>
                      <span className='text-gray-800 font-normal text-sm truncate'>
                        {host?.website || 'Enter Website URL'}
                      </span>
                    </EditableInputCellViewMode>
                  </EditableInputCell>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewHostHeader;
